import React from 'react';
import { getContentTypeIcon, getContentTypeName } from '../../utils/designTokens';

/**
 * ContentTypeSwitcher - Segmented control for switching between content types
 * Used in navigation, dashboards, and profile pages
 *
 * @param {string} activeType - Currently selected content type
 * @param {function} onChange - Callback when type changes: onChange(newType)
 * @param {array} types - Array of content types to show (default: ['prompt', 'agent', 'mcp_server'])
 * @param {boolean} showCounts - Whether to show counts for each type (default: false)
 * @param {object} counts - Object with counts for each type: { prompt: 5, agent: 3, mcp_server: 2 }
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} variant - Style variant: 'tabs', 'pills', 'underline' (default: 'tabs')
 * @param {string} className - Additional CSS classes
 */
const ContentTypeSwitcher = ({
  activeType = 'prompt',
  onChange,
  types = ['prompt', 'agent', 'mcp_server'],
  showCounts = false,
  counts = {},
  size = 'md',
  variant = 'tabs',
  className = ''
}) => {
  // Size variants
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-5 py-3 text-lg gap-2'
  };

  // Variant styles
  const variantStyles = {
    tabs: {
      container: 'inline-flex bg-gray-100 dark:bg-spooky-bg-medium rounded-lg p-1',
      button: 'rounded-md transition-all duration-200 font-medium',
      active: 'bg-white dark:bg-spooky-bg-light shadow-sm',
      inactive: 'hover:bg-gray-50 dark:hover:bg-spooky-bg-light/50 text-gray-600 dark:text-spooky-mist'
    },
    pills: {
      container: 'inline-flex gap-2',
      button: 'rounded-full border-2 transition-all duration-200 font-medium',
      active: 'shadow-sm',
      inactive: 'border-gray-300 dark:border-spooky-purple-mist hover:border-gray-400 dark:hover:border-spooky-shadow text-gray-600 dark:text-spooky-mist'
    },
    underline: {
      container: 'inline-flex gap-1 border-b border-gray-200 dark:border-spooky-purple-mist',
      button: 'border-b-2 -mb-px transition-all duration-200 font-medium',
      active: 'border-current',
      inactive: 'border-transparent hover:border-gray-300 dark:hover:border-spooky-mist text-gray-600 dark:text-spooky-mist'
    }
  };

  const styles = variantStyles[variant];

  // Get color for active button based on content type
  const getActiveColor = (type) => {
    const colorMap = {
      prompt: 'text-indigo-600 dark:text-indigo-400',
      agent: 'text-pink-600 dark:text-pink-400',
      mcp_server: 'text-cyan-600 dark:text-cyan-400'
    };
    return colorMap[type] || colorMap.prompt;
  };

  // Get background color for active button in pills/tabs variant
  const getActiveBg = (type) => {
    if (variant === 'underline') return '';

    const bgMap = {
      prompt: 'bg-indigo-50 dark:bg-indigo-900/20',
      agent: 'bg-pink-50 dark:bg-pink-900/20',
      mcp_server: 'bg-cyan-50 dark:bg-cyan-900/20'
    };
    return bgMap[type] || bgMap.prompt;
  };

  // Get border color for pills variant
  const getPillsBorder = (type) => {
    if (variant !== 'pills') return '';

    const borderMap = {
      prompt: 'border-indigo-500 dark:border-indigo-400',
      agent: 'border-pink-500 dark:border-pink-400',
      mcp_server: 'border-cyan-500 dark:border-cyan-400'
    };
    return borderMap[type] || borderMap.prompt;
  };

  const handleTypeChange = (type) => {
    if (onChange) {
      onChange(type);
    }
  };

  return (
    <div
      className={`${styles.container} ${className}`}
      role="tablist"
      aria-label="Content type switcher"
    >
      {types.map((type) => {
        const isActive = type === activeType;
        const icon = getContentTypeIcon(type);
        const label = getContentTypeName(type, showCounts && counts[type] > 0);
        const count = counts[type] || 0;

        return (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${type}-panel`}
            onClick={() => handleTypeChange(type)}
            className={`
              inline-flex items-center justify-center
              ${sizeClasses[size]}
              ${styles.button}
              ${isActive ? styles.active : styles.inactive}
              ${isActive ? getActiveColor(type) : ''}
              ${isActive ? getActiveBg(type) : ''}
              ${isActive && variant === 'pills' ? getPillsBorder(type) : ''}
            `}
          >
            <span aria-hidden="true">{icon}</span>
            <span>{label}</span>
            {showCounts && count > 0 && (
              <span
                className={`
                  ml-1.5 px-2 py-0.5 rounded-full text-xs font-semibold
                  ${isActive
                    ? 'bg-white/50 dark:bg-black/20'
                    : 'bg-gray-200 dark:bg-spooky-bg-light text-gray-700 dark:text-spooky-ghost-white'
                  }
                `}
                aria-label={`${count} ${label.toLowerCase()}`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ContentTypeSwitcher;
